/*
 * © 2026 Ecsper Ltd. All rights reserved.
 * SPDX-License-Identifier: LicenseRef-Ecsper-Proprietary
 * Unauthorised use, copying, or distribution is strictly prohibited.
 */

import type { ReactNode } from 'react';

import { formatRelativeTime } from './format-relative-time.js';

/**
 * Save state derived from auto-save loop in host-word (Task 2.16).
 * - idle: no recent save activity
 * - saving: save in flight
 * - saved: last save successful; timestamp shown
 * - error: last save failed; retry button shown
 */
export type SaveState = 'idle' | 'unsaved' | 'saving' | 'saved' | 'error';

export interface SaveStatusProps {
  state: SaveState;
  /** ISO timestamp of last successful save. */
  lastSavedAt?: string;
  /** Optional error message for state='error'. */
  errorMessage?: string;
  /** Called when user clicks retry. */
  onRetry?: () => void;
}

function IdleIcon() {
  return (
    <svg
      className="ecs-save-status__icon-svg"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      aria-hidden="true"
    >
      <circle cx="12" cy="12" r="9" />
      <path strokeLinecap="round" d="M12 8v4l3 2" />
    </svg>
  );
}

function SavingIcon() {
  return (
    <svg
      className="ecs-save-status__icon-svg"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      aria-hidden="true"
    >
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v4M12 16v4M4 12h4M16 12h4" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  );
}

function SavedIcon() {
  return (
    <svg
      className="ecs-save-status__icon-svg"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      aria-hidden="true"
    >
      <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
      <circle cx="12" cy="12" r="9" />
    </svg>
  );
}

function ErrorIcon() {
  return (
    <svg
      className="ecs-save-status__icon-svg"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      aria-hidden="true"
    >
      <circle cx="12" cy="12" r="9" />
      <path strokeLinecap="round" d="M12 8v5M12 16h.01" />
    </svg>
  );
}

function labelForState(state: SaveState, lastSavedAt?: string): string {
  switch (state) {
    case 'idle':
      return 'Ready to save';
    case 'unsaved':
      return 'Unsaved changes';
    case 'saving':
      return 'Saving…';
    case 'saved': {
      const rel =
        lastSavedAt !== undefined && lastSavedAt !== '' ? formatRelativeTime(lastSavedAt) : '';
      return rel !== '' ? `Saved to Vault ${rel}` : 'Saved to Vault';
    }
    case 'error':
      return 'Save failed';
    default:
      return 'Unknown save state';
  }
}

/**
 * Renders the current document save state.
 * TrueRead: state conveyed by icon plus label (T2); no colour-only signal.
 */
export function SaveStatus({ state, lastSavedAt, errorMessage, onRetry }: SaveStatusProps) {
  const isError = state === 'error';
  const liveRole = isError ? 'alert' : 'status';
  const ariaLive = isError ? 'assertive' : 'polite';

  let icon: ReactNode;
  switch (state) {
    case 'idle':
    case 'unsaved':
      icon = <IdleIcon />;
      break;
    case 'saving':
      icon = <SavingIcon />;
      break;
    case 'saved':
      icon = <SavedIcon />;
      break;
    case 'error':
      icon = <ErrorIcon />;
      break;
    default:
      icon = null;
  }

  const label = labelForState(state, lastSavedAt);

  return (
    <section className={`ecs-panel ecs-save-status ecs-save-status--${state}`}>
      <div
        className="ecs-save-status__live"
        role={liveRole}
        aria-live={ariaLive}
        aria-atomic="true"
      >
        <span className="ecs-save-status__icon" aria-hidden="true">
          {icon}
        </span>
        <span className="ecs-save-status__label">{label}</span>
      </div>
      {isError && errorMessage !== undefined && errorMessage !== '' ? (
        <p className="ecs-save-status__error-detail">{errorMessage}</p>
      ) : null}
      {isError && onRetry !== undefined ? (
        <div className="ecs-save-status__actions">
          <button
            type="button"
            className="ecs-btn ecs-btn--secondary"
            onClick={() => {
              onRetry();
            }}
          >
            Retry
          </button>
        </div>
      ) : null}
    </section>
  );
}
